import { CloseIcon } from '@c/icons';
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import useNavigationEvent from '@util/hooks/useNavigationEvent';
import { cva } from 'class-variance-authority';
import { Montserrat } from 'next/font/google';
import { Fragment, Suspense, useCallback } from 'react';

const montserrat = Montserrat({
  subsets: ['latin'],
  weight: ['400', '500', '600', '700'],
});

const styles = cva(
  'text-[1.5rem] flex flex-col relative  text-left text-brand-black shadow-xl transition-all max-h-screen',
  {
    variants: {
      isFullScreen: {
        true: 'w-full h-full',
        false: 'w-fit xl:max-w-[90%] min-w-[32rem] rounded-[3rem] mx-4',
      },
      overflowType: {
        auto: 'overflow-auto',
        visible: 'overflow-y-visible',
        hidden: 'overflow-hidden',
      },
      bgTransparent: {
        true: 'bg-black/70',
        false: 'bg-white',
      },
    },
  }
);
export interface BaseModalProps {
  isOpen: boolean;
  dismiss: () => void;
  children: React.ReactNode;
  title?: string;
  hideCloseIcon?: boolean;
  isFullScreen?: boolean;
  disableBackdropDismiss?: boolean;
  overflowType?: 'auto' | 'visible' | 'hidden';
  bgTransparent?: boolean;
  className?: string;
  onBackButtonClick?: () => void;
}

function BaseModal({
  children,
  title,
  hideCloseIcon,
  isOpen,
  dismiss,
  isFullScreen = false,
  disableBackdropDismiss = false,
  overflowType = 'auto',
  bgTransparent = false,
  className,
  onBackButtonClick,
}: BaseModalProps) {
  return (
    <Transition show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className={`relative z-[60] ${className}`}
        onClose={() => dismiss?.()}
        static={disableBackdropDismiss}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {disableBackdropDismiss ? (
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          ) : (
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          )}
        </TransitionChild>

        <div
          className={`fixed inset-0 z-10 overflow-y-auto ${montserrat.className}`}
        >
          <div className="flex h-full items-center justify-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={styles({
                  isFullScreen,
                  overflowType,
                  bgTransparent,
                })}
              >
                <div className={`relative flex w-full items-center sm:mt-0`}>
                  {onBackButtonClick && (
                    <button
                      type="button"
                      className="z-50 pl-8 text-[1.8rem] font-semibold text-brand-secondary transition-colors duration-75 hover:text-brand-secondary-darker"
                      onClick={onBackButtonClick}
                    >
                      Back
                    </button>
                  )}

                  <div
                    className={`flex w-full items-center justify-between ${
                      !title && hideCloseIcon ? '' : 'py-8 pl-6'
                    } ${onBackButtonClick ? 'pl-0' : ''}`}
                  >
                    {!!title && (
                      <DialogTitle
                        className={`absolute left-0 right-0 top-6 mx-auto w-full whitespace-nowrap text-center text-[2rem] font-semibold text-black ${montserrat.className}`}
                      >
                        {title}
                      </DialogTitle>
                    )}

                    {!hideCloseIcon && (
                      <div className="z-10 flex w-full justify-end">
                        <Suspense>
                          <BaseModalCloseButton
                            dismiss={dismiss}
                            bgTransparent={bgTransparent}
                            isFullScreen={isFullScreen}
                          />
                        </Suspense>
                      </div>
                    )}
                  </div>

                  {title && !hideCloseIcon && (
                    <div className="absolute bottom-0 left-0 right-0 h-px w-full bg-zinc-200" />
                  )}
                </div>

                {children}
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

const BaseModalCloseButton = ({
  dismiss,
  bgTransparent,
  isFullScreen,
}: {
  dismiss: () => void;
  bgTransparent: boolean;
  isFullScreen: boolean;
}) => {
  useNavigationEvent(
    useCallback(({ url, previousUrl }) => {
      if (!url || !previousUrl) return;
      if (url !== previousUrl) {
        dismiss();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  );
  return (
    <button
      className={`relative z-50 mr-6 flex h-16 w-16 items-center justify-center rounded-full border border-gray-200 bg-white/90 shadow-lg transition-opacity hover:border-gray-300 hover:bg-white active:opacity-80 ${
        bgTransparent ? 'text-white' : 'text-black'
      }`}
      type="button"
      onClick={() => dismiss()}
    >
      <CloseIcon />
    </button>
  );
};

export default BaseModal;
